import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LevelResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Linha de tabela de nível de acesso (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + box slate com escudo + menu ••• (última col).
 */
export interface LevelListItemProps {
  /** Level data from API */
  level: LevelResponse;
  /** Se a linha está selecionada (bulk) */
  selected?: boolean;
  /** Callback de toggle de seleção */
  onToggleSelect?: (id: string) => void;
  /** Callback when delete is clicked */
  onDelete?: (id: string) => void;
  /** Callback when activate/deactivate is clicked */
  onToggleStatus?: (id: string, currentStatus: string) => void;
  /** Navega para a lista de usuários filtrada por este nível */
  onViewUsers?: (id: string) => void;
  /** Duplica o nível (recebe o objeto completo p/ montar a cópia) */
  onDuplicate?: (level: LevelResponse) => void;
}

export const LevelListItem: React.FC<LevelListItemProps> = ({
  level,
  selected = false,
  onToggleSelect,
  onDelete,
  onToggleStatus,
  onViewUsers,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  const modulesCount = Object.keys(level.permissions || {}).length;
  const isActive = level.status === 'active';

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => navigate(`/settings/levels/${level.id}/edit`) },
  ];
  if (onViewUsers) actions.push({ label: 'Ver usuários', icon: 'users', onClick: () => onViewUsers(level.id) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(level) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(level.id, level.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(level.id), variant: 'danger', dividerBefore: true });
  }

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`level-row-${level.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(level.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`level-check-${level.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Nome: box slate com escudo + nome (F44: entidade = box rounded-lg 36) */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#64748b' }}
          >
            <Icon name="shield" className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {level.name}
            </div>
          </div>
        </div>
      </td>

      {/* Descrição */}
      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
        <div className="text-sm max-w-xs truncate">{level.description || 'Sem descrição'}</div>
      </td>

      {/* Módulos */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          <Icon name="layers" className="w-3 h-3" />
          {modulesCount} {modulesCount === 1 ? 'módulo' : 'módulos'}
        </span>
      </td>

      {/* Status — F47: switch inline (Switch shared sm); pill só quando não togglável (F62) */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`level-toggle-${level.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(level.id, level.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Inativo'}
            data-testid={`level-status-${level.id}`}
          />
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`level-row-actions-${level.id}`} />
      </td>
    </tr>
  );
};

LevelListItem.displayName = 'LevelListItem';
